import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { SortingState } from "@tanstack/react-table";

import {
  DepartmentExportsCSV,
  fetchDepartment as OnfetchDepartment,
  updateDepartment as onUpdateStatusInDepartment,
} from "slices/department/thunk";
import { toast } from "react-toastify";
import TableContainer from "Common/TableContainer";
import { app_modules } from "helpers/apps_modules_list";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";

const DepartmentList = () => {
  document.title = "Department List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  //* Selector for department list
  const selectOrganisation = createSelector(
    (state: any) => state.department,
    (department) => ({
      department: department.departmentList,
      rowCount: department.rowCount,
      loading: department.loading,
      error: department.error,
    })
  );

  const { department, rowCount, loading, error } =
    useSelector(selectOrganisation);

  //* Get user from Redux or localStorage
  const userFromRedux = useSelector((state: any) => state.Login.user);
  const localUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = userFromRedux || localUser;

  //* Permissions
  const user_permissions = user?.permissions || JSON.parse(localStorage.getItem("userPermissions") || "[]");
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.user_management?.app_name
  );
  const department_permissions = master_permissions?.modules_permissions?.find(
    (item: any) => item?.modules_name === app_modules?.user_management?.modules?.department
  );

  console.log("Redux User:", userFromRedux);
  console.log("LocalStorage authUser:", localUser);
  console.log("User Permissions:", user_permissions);
  console.log("Master Permissions:", master_permissions);
  console.log("Department Permissions:", department_permissions);

  //* Pagination and filters
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "0");
  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  //* Fetch departments
  useEffect(() => {
    dispatch(
      OnfetchDepartment({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: columnFilters,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, columnFilters, colValue]);

  //* Reset pageIndex on filters/sorting change
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters, search]);

  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page: newPage.toString() });
  };

  //* Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  //* Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
      }
    };
    fetchPermissions();
  }, [dispatch]);
  
 //* Toggle department status
  const handleStatusToggle = async (org: any) => {
    try {
      const updatedStatus = !org.department_status;
      const update_payload = {
        ...org,
        department_id: org.department_id || org.id,
        department_status: updatedStatus,
      };
      await dispatch(onUpdateStatusInDepartment(update_payload)).unwrap();
      toast.success(
        `Department ${updatedStatus ? "activated" : "deactivated"} successfully`
      );
      dispatch(
        OnfetchDepartment({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err) {
      toast.error("Failed to update status");
    }
  };


  //* Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  //* Handle CSV export
  const handleExport = async () => {
    try {
      const { blob, filename } = await DepartmentExportsCSV({
        search,
        filters: columnFilters,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  //* Table columns
  const columns = useMemo(() => {
    const DynamicAction = department_permissions?.permissions?.Update
      ? [{
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => (
            <Link
              to={`/department-form/${row.original.department_id}`}
              state={{ departemnt: row.original }}
              className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
            >
              <i className="ph-pencil"></i>
            </Link>
          )
        }]
      : [];

    return [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no",
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => row.index + 1 + pageIndex * pageSize,
      // },
     {
        header: "Department",
        accessorKey: "department_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/department/column-filter-dropdown/?column_filter=department_name",
        //  filterFn: exactMatchFilter,
      },
      {
        header: "Organisation",
        accessorKey: "organization__organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "/api/department/column-filter-dropdown/?column_filter=organization__organization_name",
      },
           {
        header: "Status",
        accessorKey: "department_status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(row?.original)}
            >
              <i
                className={`bi ${
                  org.department_status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                }`}
                style={{ color: "#0073BB" }}
              />
            </div>
          );
        },
      },

      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },

      ...DynamicAction,
    ];
  }, [department_permissions, pageIndex, pageSize]);

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Department</h6>
                    </Col>
                    <Col xl={2} md={3}>
                      <SearchBox value={searchInput} onChange={setSearchInput} />
                    </Col>
                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        {department_permissions?.permissions?.Create && (
                          <Link
                            to="/department-form"
                            className="btn"
                            style={{
                              backgroundColor: "#0073BB",
                              borderColor: "#0073BB",
                              color: "#fff",
                            }}
                          >
                            <i className="bi bi-plus-circle align-baseline me-1"></i>
                            Add Department
                          </Link>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={department || []}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting
                      isBordered={false}
                      isPagination
                      isGlobalFilter={false}
                      SearchPlaceholder="Search Department..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default DepartmentList;
